import React, { useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useProperties from "hooks/useProperties";
import { capitalizeWord, tickFormatter } from "utils/commonUtils";
import { ReactComponent as MapIcon } from "assets/icons/location_big.svg";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";

const renderCustomizedLabel = (props) => {
  const { x, y, width, height, value } = props;

  const numericValue = Number(value);
  const formattedValue = Math.round(numericValue).toLocaleString();

  return (
    <g>
      <text
        x={x + width / 2}
        y={y - 15}
        fill="#525866"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fontWeight={500}
      >
        {`${formattedValue}mq`}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip bg-white p-2 border rounded-lg shadow-sm ">
        <h4>{payload[0].payload.location}</h4>
        <p className="">
          Media Mq:{" "}
          <span className="text-primary font-bold">
            {Math.round(payload[0].value)}
          </span>
        </p>
      </div>
    );
  }

  return null;
};

const AverageSquareMeterChart = ({
  data = null,
  tooltipId = "media_metro_tooltip",
  tooltipContent = "Qui sono mostrati i metri quadri medi delle proprietà in vendita sulle zone selezionate. Questi dati vengono recuperati da oltre 100 piattaforme immobiliari che monitoriamo.",
  emptyStateMessage = "Seleziona una zona per visualizzarne le statistiche di mercato",
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { dashboardMarketStatisticsProperties } = useProperties();

  // Use provided data or fallback to dashboard data
  const sourceData = data || dashboardMarketStatisticsProperties;

  // Transform the data for the chart
  const chartData = sourceData.map((item) => ({
    location: capitalizeWord(item.locationName), // Use locationName for X-axis
    value: parseFloat(item.averageArea), // Use averageArea as the value
  }));

  return (
    <div className="bg-white flex-1 rounded-[16px] px-4 pb-4 pt-[21.5px] h-[288px] border-[1px] border-[#E2E4E9]">
      {chartData?.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[16px] font-semibold leading-5 text-[#1A202C] -tracking-[0.176px]">
              Media Metro Quadro
            </h2>

            <div
              data-tooltip-id={tooltipId}
              data-tooltip-content={tooltipContent}
              className="cursor-pointer"
            >
              <InfoIcon />
            </div>

            {/* Info Tooltip */}
            <ReactTooltip
              id={tooltipId}
              place="top"
              className="!max-w-96 !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-sm"
            />
          </div>
          <div className="h-[200px] pt-4">
            <ResponsiveContainer
              className={"average_area_chart"}
              width="100%"
              height="100%"
            >
              <BarChart
                data={chartData}
                barSize={56.55}
                margin={{ top: 0, right: 0, left: 0, bottom: -10 }}
              >
                <XAxis
                  dataKey="location"
                  tickLine={false}
                  tickMargin={12}
                  axisLine={false}
                  tickFormatter={tickFormatter}
                  tick={{ fontSize: 10, color: "#525866", fontWeight: 400 }}
                  interval={0}
                />
                <YAxis width={0} hide />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "transparent" }}
                />
                <Bar dataKey="value" radius={20}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="#1DA1F2"
                      opacity={index === hoveredIndex ? "1" : "0.3"}
                      onMouseEnter={() => setHoveredIndex(index)}
                    />
                  ))}

                  <LabelList
                    dataKey="value"
                    content={(props) =>
                      renderCustomizedLabel({ ...props, hoveredIndex })
                    }
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="flex h-full items-center justify-center flex-col">
          <MapIcon className="w-[63px]  text-[#1DA1F266]" />
          <p className="text-[16px] text-center mt-4 font-semibold leading-[20px] text-[#A5D9FA]">
            {emptyStateMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default AverageSquareMeterChart;
